"use client";

import * as React from "react";
import theme, { blueGradientSxDown } from "@/theme/theme";
import Grid from "@mui/material/Grid";
import { Box, Stack, Typography, keyframes } from "@mui/material";
import { TESTIMONIALS_MOCK } from "@/data/testimonials";
import TestimonialCard from "@/components/home/testimonialsCard";

// Animation helpers
const scrollUp = keyframes`
  0%   { transform: translateY(0); }
  100% { transform: translateY(-50%); }  /* we duplicate content so -50% is a perfect loop */
`;
const scrollDown = keyframes`
  0%   { transform: translateY(-50%); }
  100% { transform: translateY(0); }
`;

// Utility: splitting cards into N columns for a masonry-like layout
function chunkIntoColumns<T>(items: T[], columns: number) {
  const out: T[][] = Array.from({ length: columns }, () => []);
  items.forEach((item, i) => out[i % columns].push(item));
  return out;
}

export default function Testimonials() {

  const cards = [...TESTIMONIALS_MOCK, ...TESTIMONIALS_MOCK];

  const columns = 3;
  const cols = chunkIntoColumns(cards, columns);

  const durations = [144, 128, 160];
  const directions = [scrollUp, scrollDown, scrollUp];

  return (
    <Stack width="100%" alignItems="center" position="relative" py={8} px={{ xs: 2, md: 10 }} gap={10}>
      <Stack width="100%" alignItems="center" gap={2}>
        <Typography
          variant="h2"
          fontSize={"2.75rem !important"}
          fontWeight={400}
          className="chno"
          color={theme.palette.brand.mgsBlue1.main}
          textAlign="center"
        >
          <Box
            component="span"
            sx={{
                ...blueGradientSxDown(theme),
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
            }}
          >
            What our customers say
          </Box>
        </Typography>
        <Typography variant="h5" fontWeight={300} color={theme.palette.text.secondary} textAlign="center">
          See what our customers have to say about us.
        </Typography>
      </Stack>

      {/* marquee viewport */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 1400,
          mx: "auto",
          overflow: "hidden",
        }}
      >
        {/* top & bottom gradients */}
        <Box sx={{
          position: "absolute", top: 0, left: 0, width: "100%", height: 80,
          background: "linear-gradient(180deg, #0B0B0B 0%, rgba(11, 11, 11, 0) 100%)",
          zIndex: 2, pointerEvents: "none",
        }} />
        <Box sx={{
          position: "absolute", bottom: 0, left: 0, width: "100%", height: 120,
          background: "linear-gradient(0deg, #0B0B0B 0%, rgba(11, 11, 11, 0) 100%)",
          zIndex: 2, pointerEvents: "none",
        }} />

        {/* columns container */}
        <Grid container columnSpacing={{ xs: 2, md: 3 }} rowSpacing={0} px={{ xs: 2, md: 0 }} position="relative" zIndex={1}>
          {cols.map((colItems, idx) => (
            <Grid key={idx} size={{ xs: 12, md: 6, lg: 4 }}>
              <Box
                sx={{
                  height: { xs: 520, sm: 560, md: 640 },
                  overflow: "hidden",
                  position: "relative",
                  isolation: "isolate",
                }}
              >
                {/* column content */}
                <Stack
                  spacing={{ xs: 2, md: 3 }}
                  sx={{
                    animation: `${directions[idx]} ${durations[idx]}s linear infinite`,
                    willChange: "transform",
                    "&:hover": { animationPlayState: "paused" },
                    pr: { md: 1 },
                  }}
                >
                  {colItems.map((item, i) => (
                    <Box key={`col-${idx}-orig-${item.id}-${i}`}>
                      <TestimonialCard data={item} />
                    </Box>
                  ))}
                  {colItems.map((item, i) => (
                    <Box key={`col-${idx}-clone-${item.id}-${i}`}>
                      <TestimonialCard data={item} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  );
}
